import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import {
  getIreferGuideline,
  getIreferCredentials,
  logIreferAccess
} from '../../../services/ireferService';

const EvidenceGuidelinesSection = ({ recommendation }) => {
  const { user } = useAuth();
  const [ireferGuideline, setIreferGuideline] = useState(null);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIreferGuideline();
    checkCredentials();
  }, [recommendation?.ireferCode]);

  const loadIreferGuideline = async () => {
    if (!recommendation?.ireferCode) {
      setLoading(false);
      return;
    }

    try {
      const data = await getIreferGuideline(recommendation?.ireferCode);
      
      if (!data) {
        console.warn(`No iRefer guideline found for code: ${recommendation?.ireferCode}`);
        setIreferGuideline(null);
      } else {
        setIreferGuideline(data);
      }
    } catch (error) {
      console.error('Error loading iRefer guideline:', error);
      setIreferGuideline(null);
    } finally {
      setLoading(false);
    }
  };

  const checkCredentials = async () => {
    if (!user?.id) return;

    try {
      const data = await getIreferCredentials(user?.id);
      setHasCredentials(data?.verified && data?.autoLogin);
    } catch (error) {
      console.error('Error checking credentials:', error);
    }
  };

  const openIreferGuideline = async () => {
    if (!ireferGuideline?.ireferUrl) return;

    try {
      // Log access
      if (user?.id) {
        await logIreferAccess({
          userId: user?.id,
          recommendationId: recommendation?.id,
          ireferCode: recommendation?.ireferCode,
          accessType: hasCredentials ? 'auto_login' : 'manual',
          autoLoginSuccess: hasCredentials
        });
      }

      // Open guideline
      window.open(ireferGuideline?.ireferUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening iRefer guideline:', error);
      window.open(ireferGuideline?.ireferUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="evidence-section bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!ireferGuideline) {
    return (
      <div className="evidence-section bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="BookOpen" size={20} color="var(--color-primary)" />
          📚 Evidence & Guidelines
        </h3>
        <p className="text-sm text-muted-foreground">
          Evidence Grade: {recommendation?.evidenceGrade || 'A'}
        </p>
      </div>
    );
  }

  return (
    <div className="evidence-section bg-card border border-border rounded-lg p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <Icon name="BookOpen" size={20} color="var(--color-primary)" />
        📚 Evidence & Guidelines
      </h3>

      <div className="irefer-guideline-card bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-2 border-blue-300 dark:border-blue-700 rounded-xl p-6">
        <div className="guideline-header flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="irefer-badge bg-primary text-primary-foreground px-3 py-1.5 rounded-lg font-semibold text-sm">
              iRefer {ireferGuideline?.ireferCode}
            </span>
            {hasCredentials && (
              <span className="auto-login-badge bg-green-500 text-white px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1">
                <Icon name="Zap" size={14} />
                🔓 Auto-Login
              </span>
            )}
          </div>
          {ireferGuideline?.evidenceGrade && (
            <span className="bg-white dark:bg-gray-800 px-3 py-1 rounded-lg text-xs font-semibold text-foreground border border-border">
              Grade {ireferGuideline?.evidenceGrade}
            </span>
          )}
        </div>

        <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
          {ireferGuideline?.title}
        </h4>

        <p className="category text-sm text-blue-700 dark:text-blue-300 mb-3">
          {ireferGuideline?.category} › {ireferGuideline?.bodyRegion}
        </p>

        <div className="guideline-details space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <span className="font-semibold">Modality:</span>
            <span>{ireferGuideline?.modality}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground">
            <span className="font-semibold">Evidence Grade:</span>
            <span>{ireferGuideline?.evidenceGrade}</span>
          </div>
        </div>

        {ireferGuideline?.clinicalScenario && (
          <div className="clinical-indication bg-white/50 dark:bg-gray-800/50 rounded-lg p-4 mb-4">
            <h5 className="text-sm font-semibold text-foreground mb-2">Clinical Indication:</h5>
            <p className="text-sm text-muted-foreground">"{ireferGuideline?.clinicalScenario}"</p>
          </div>
        )}

        <button
          onClick={openIreferGuideline}
          className="btn-view-guideline w-full bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-lg font-semibold transition-all flex items-center justify-between group"
        >
          <span>
            📖 {hasCredentials ? 'View Full Guideline (Auto-Login)' : 'View on iRefer.co.uk'} ↗
          </span>
          <Icon
            name="ExternalLink"
            size={18}
            className="group-hover:translate-x-1 transition-transform"
          />
        </button>

        {!hasCredentials && (
          <p className="tip text-xs text-center mt-3 text-muted-foreground">
            💡 <a href="/account-settings?tab=irefer" className="text-primary hover:underline">Connect iRefer</a> for auto-login
          </p>
        )}
      </div>

      <small className="block mt-4 text-xs text-muted-foreground">
        Source: Royal College of Radiologists iRefer
      </small>
    </div>
  );
};

export default EvidenceGuidelinesSection;